import { create } from 'zustand';
import { produce } from 'immer';
import { customStylesApi } from '@/lib/api/custom-styles';
import { dedupeCustomStyles } from '@/lib/asset-entity-lists';
import { getAuthScopeRevision, registerAuthScopedCacheReset } from '@/lib/auth-scoped-state-reset';
import type { CreateCustomStyleRequest, CustomStyle, Creativity, StyleGenerationPreference } from '@/types/custom-style';

interface CustomStylesState {
  creativityPreferences: Record<string, StyleGenerationPreference>;
  creativitySaving: Record<string, boolean>;
  creativityErrors: Record<string, string>;
  creativityDrafts: Record<string, Creativity>;
  saveCreativity: (styleId: string, value: Creativity, options?: {supersedeConversation?: boolean; loadPreference?: boolean}) => Promise<void>;
  styles: CustomStyle[];
  isLoading: boolean;
  isCreating: boolean;
  error: string | null;
  lastLoadedAt: number | null;
  ensureLoaded: () => Promise<void>;
  refresh: () => Promise<void>;
  createStyle: (payload: CreateCustomStyleRequest) => Promise<CustomStyle>;
  retryStyleAnalysis: (id: string) => Promise<CustomStyle>;
  archiveStyle: (id: string) => Promise<void>;
  clearError: () => void;
  registerSavedStyle: (style: CustomStyle) => void;
}

let listRequest: Promise<void> | null = null;
const removedStyles = new Set<string>();
const archiveRequests = new Map<string, Promise<void>>();
const STYLE_LIST_TTL_MS = 60_000;
// One request per style at a time. A newer committed gesture replaces the queued value.
const preferenceWrites = new Map<string, { value: Creativity; supersede: boolean; promise: Promise<void> }>();
const CUSTOM_STYLE_ERROR = {
  LOAD_FAILED: 'customStyleLoadFailed',
  CREATE_FAILED: 'customStyleCreateFailed',
  RETRY_FAILED: 'customStyleRetryAnalysisFailed',
  DELETE_FAILED: 'customStyleDeleteFailed',
} as const;

export const useCustomStylesStore = create<CustomStylesState>((set, get) => ({
  creativityPreferences: {},
  creativitySaving: {},
  creativityErrors: {},
  creativityDrafts: {},
  styles: [],
  isLoading: false,
  isCreating: false,
  error: null,
  lastLoadedAt: null,

  saveCreativity: (id, value, options) => {
    const queued = preferenceWrites.get(id);
    if (queued) {
      queued.value = value;
      queued.supersede ||= Boolean(options?.supersedeConversation);
      set(produce<CustomStylesState>(draft => { draft.creativityDrafts[id] = value; }));
      return queued.promise;
    }
    const saved = get().creativityPreferences[id]
      ?? get().styles.find(style => style.id === id)?.generation_preference;
    if (saved?.creativity === value && !options?.supersedeConversation) {
      set(produce<CustomStylesState>(draft => { delete draft.creativityErrors[id]; delete draft.creativityDrafts[id]; }));
      return Promise.resolve();
    }
    const scope = getAuthScopeRevision();
    const entry = { value, supersede: Boolean(options?.supersedeConversation), promise: Promise.resolve() };
    preferenceWrites.set(id, entry);
    set(produce<CustomStylesState>(draft => {
      draft.creativitySaving[id] = true;
      draft.creativityDrafts[id] = value;
      delete draft.creativityErrors[id];
    }));
    entry.promise = (async () => {
      // Defer one microtask so duplicate blur/pointerup commits share the same promise.
      await Promise.resolve();
      try {
        // A homepage draft can arrive before the authenticated style list has loaded.
        // Keep the existing saving/error gate active throughout this initial read.
        if (options?.loadPreference) await get().ensureLoaded();
        while (true) {
          if (scope !== getAuthScopeRevision()) throw new Error('customStyleCreativitySaveFailed');
          const sending = entry.value;
          const current = get().creativityPreferences[id]
            ?? get().styles.find(style => style.id === id)?.generation_preference;
          const response = await customStylesApi.saveGenerationPreference(id, sending, {
            expected_revision: current?.revision ?? 0,
            supersede_conversation: entry.supersede,
          });
          if (scope !== getAuthScopeRevision()) throw new Error('customStyleCreativitySaveFailed');
          if (response.error || !response.data) {
            // Refresh authoritative revision before the user retries a concurrent edit.
            await get().refresh();
            throw new Error('customStyleCreativitySaveFailed');
          }
          const result = response.data;
          set(produce<CustomStylesState>(draft => {
            if ((draft.creativityPreferences[id]?.revision ?? 0) > result.generation_preference.revision) return;
            draft.creativityPreferences[id] = result.generation_preference;
            const style = draft.styles.find(item => item.id === id);
            if (style) {
              style.generation_preference = result.generation_preference;
              style.generation_defaults = result.generation_defaults;
            }
          }));
          if (entry.value === sending) {
            set(produce<CustomStylesState>(draft => { delete draft.creativityDrafts[id]; }));
            break;
          }
        }
      } catch (error) {
        if (scope === getAuthScopeRevision()) {
          set(produce<CustomStylesState>(draft => {
            draft.creativityErrors[id] = 'customStyleCreativitySaveFailed';
          }));
        }
        throw error;
      } finally {
        if (preferenceWrites.get(id) === entry) {
          preferenceWrites.delete(id);
          set(produce<CustomStylesState>(draft => { delete draft.creativitySaving[id]; }));
        }
      }
    })();
    return entry.promise;
  },

  registerSavedStyle: (style) => set(produce<CustomStylesState>((draft) => {
    draft.styles = dedupeCustomStyles([style, ...draft.styles.filter(item => item.id !== style.id)]);
    draft.error = null;
  })),

  ensureLoaded: async () => {
    const state = get();
    if (listRequest) return listRequest;
    if (state.lastLoadedAt && Date.now() - state.lastLoadedAt < STYLE_LIST_TTL_MS) return;
    await get().refresh();
  },

  refresh: () => {
    if (listRequest) return listRequest;
    const scope = getAuthScopeRevision();
    const request = (async () => {
      const stylesAtStart = new Map(get().styles.map(style => [style.id, style]));
      set(produce<CustomStylesState>((draft) => {
        draft.isLoading = true;
        draft.error = null;
      }));
      try {
        const response = await customStylesApi.list();
        if (scope !== getAuthScopeRevision()) return;
        if (response.error || !response.data) {
          set(produce<CustomStylesState>((draft) => {
            draft.isLoading = false;
            draft.error = CUSTOM_STYLE_ERROR.LOAD_FAILED;
          }));
          return;
        }
        const styles = dedupeCustomStyles(response.data).filter(style => !removedStyles.has(style.id));
        // A save may finish while this older list request is in flight.
        const savedDuringLoad = get().styles.filter(style => stylesAtStart.get(style.id) !== style);
        set(produce<CustomStylesState>((draft) => {
          draft.styles = dedupeCustomStyles([...savedDuringLoad, ...styles.filter(style => !savedDuringLoad.some(saved => saved.id === style.id))]);
          for (const style of draft.styles) {
            const preference = style.generation_preference;
            const cached = draft.creativityPreferences[style.id];
            if (preference && (!cached || preference.revision >= cached.revision)) {
              draft.creativityPreferences[style.id] = preference;
            }
          }
          draft.isLoading = false;
          draft.error = null;
          draft.lastLoadedAt = Date.now();
        }));
      } catch {
        if (scope !== getAuthScopeRevision()) return;
        set(produce<CustomStylesState>((draft) => {
          draft.isLoading = false;
          draft.error = CUSTOM_STYLE_ERROR.LOAD_FAILED;
        }));
      }
    })();
    listRequest = request;
    void request.finally(() => { if (listRequest === request) listRequest = null; });
    return request;
  },

  createStyle: async (payload) => {
    set(produce<CustomStylesState>((draft) => {
      draft.isCreating = true;
      draft.error = null;
    }));
    try {
      const response = await customStylesApi.create(payload);
      if (response.error || !response.data) {
        set(produce<CustomStylesState>((draft) => {
          draft.isCreating = false;
          draft.error = CUSTOM_STYLE_ERROR.CREATE_FAILED;
        }));
        throw new Error(CUSTOM_STYLE_ERROR.CREATE_FAILED);
      }
      set(produce<CustomStylesState>((draft) => {
        draft.styles = dedupeCustomStyles([response.data!, ...draft.styles.filter((style) => style.id !== response.data!.id)]);
        draft.isCreating = false;
        draft.error = null;
        draft.lastLoadedAt = Date.now();
      }));
      return response.data;
    } catch {
      set(produce<CustomStylesState>((draft) => {
        draft.isCreating = false;
        draft.error = CUSTOM_STYLE_ERROR.CREATE_FAILED;
      }));
      throw new Error(CUSTOM_STYLE_ERROR.CREATE_FAILED);
    }
  },

  retryStyleAnalysis: async (id) => {
    try {
      const response = await customStylesApi.retryAnalysis(id);
      if (response.error || !response.data) {
        throw new Error(CUSTOM_STYLE_ERROR.RETRY_FAILED);
      }
      set(produce<CustomStylesState>((draft) => {
        draft.styles = dedupeCustomStyles(draft.styles.map((style) => (style.id === id ? response.data! : style)));
        draft.error = null;
        draft.lastLoadedAt = Date.now();
      }));
      return response.data;
    } catch {
      set(produce<CustomStylesState>((draft) => {
        draft.error = CUSTOM_STYLE_ERROR.RETRY_FAILED;
      }));
      throw new Error(CUSTOM_STYLE_ERROR.RETRY_FAILED);
    }
  },

  archiveStyle: (id) => {
    const running = archiveRequests.get(id);
    if (running) return running;
    const scope = getAuthScopeRevision();
    const previous = get().styles.find(style => style.id === id);
    removedStyles.add(id);
    set(produce<CustomStylesState>(draft => {
      draft.styles = draft.styles.filter(style => style.id !== id);
      draft.error = null;
    }));
    let request!: Promise<void>;
    request = (async () => {
      await Promise.resolve();
      try {
        const response = await customStylesApi.archive(id);
        if (response.error) throw new Error(CUSTOM_STYLE_ERROR.DELETE_FAILED);
        if (scope !== getAuthScopeRevision()) return;
        set(produce<CustomStylesState>(draft => { draft.lastLoadedAt = Date.now(); }));
      } catch {
        if (scope === getAuthScopeRevision()) {
          removedStyles.delete(id);
          set(produce<CustomStylesState>(draft => {
            if (previous && !draft.styles.some(style => style.id === id)) draft.styles = dedupeCustomStyles([...draft.styles, previous]);
            draft.error = CUSTOM_STYLE_ERROR.DELETE_FAILED;
          }));
        }
        throw new Error(CUSTOM_STYLE_ERROR.DELETE_FAILED);
      } finally {
        if (archiveRequests.get(id) === request) archiveRequests.delete(id);
      }
    })();
    archiveRequests.set(id, request);
    return request;
  },

  clearError: () => {
    set(produce<CustomStylesState>((draft) => {
      draft.error = null;
    }));
  },
}));

registerAuthScopedCacheReset('custom-styles', () => {
  listRequest = null;
  removedStyles.clear();
  archiveRequests.clear();
  preferenceWrites.clear();
  useCustomStylesStore.setState({
    styles: [], isLoading: false, isCreating: false, error: null, lastLoadedAt: null,
    creativityPreferences: {}, creativitySaving: {}, creativityErrors: {}, creativityDrafts: {},
  });
});
